import * as Sentry from '@sentry/react';

/**
 * Sentry Error Monitoring
 *
 * To activate:
 * 1. Create a free account at https://sentry.io/signup/
 * 2. Create a new project → select "React"
 * 3. Copy the DSN string (looks like https://abc123@o456.ingest.sentry.io/789)
 * 4. Replace the placeholder DSN below
 *
 * In production you'd use an env variable (VITE_SENTRY_DSN),
 * but for a single-deploy app a hardcoded DSN is fine — it's
 * a write-only ingest key, not a secret.
 */

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

export function initSentry() {
  // Only initialize if a DSN is configured
  if (!SENTRY_DSN) {
    if (import.meta.env.DEV) {
      console.log('[Sentry] No DSN configured — error monitoring disabled. Set VITE_SENTRY_DSN to enable.');
    }
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,

    // Environment tagging
    environment: import.meta.env.DEV ? 'development' : 'production',

    // Release tracking — matches the Vite build hash for source map correlation
    release: import.meta.env.VITE_SENTRY_RELEASE || `dlp-tracker@${Date.now()}`,

    // Sample 100% of errors (free tier = 5K/month, more than enough)
    sampleRate: 1.0,

    integrations: [
      // Capture console.error and DOM/fetch events as breadcrumbs
      Sentry.breadcrumbsIntegration({ console: true, dom: true, fetch: true }),
    ],

    // Drop known non-app errors before they even reach beforeSend
    ignoreErrors: [
      // Browser extension noise (LastPass, Dashlane, password managers)
      /Object Not Found Matching Id/,
      // Common non-errors from injected scripts
      /MethodName:/,
      // Chrome extensions messaging
      /Extension context invalidated/,
      // Safari WebKit private browsing
      /QuotaExceededError/,
      // Browser extension SW interception (wrsParams wrapper)
      /wrsParams/,
    ],

    // Don't report errors in development
    enabled: import.meta.env.PROD,

    // Filter out noise
    beforeSend(event) {
      // Ignore network errors from the ThemeParks API (expected when offline)
      if (event.exception?.values?.some(v =>
        v.value?.includes('api.themeparks.wiki') ||
        v.value?.includes('Failed to fetch') ||
        v.value?.includes('NetworkError') ||
        v.value?.includes('Load failed')
      )) {
        return null;
      }

      // Ignore chunk/module loading errors — these are auto-handled by a hard reload
      // after a new deployment invalidates cached chunk URLs (stale HTML).
      if (event.exception?.values?.some(v =>
        v.value?.includes('Importing a module script failed') ||
        v.value?.includes('Failed to fetch dynamically imported module') ||
        v.value?.includes('error loading dynamically imported module')
      )) {
        return null;
      }

      // Ignore ResizeObserver errors (browser noise)
      if (event.exception?.values?.some(v =>
        v.value?.includes('ResizeObserver')
      )) {
        return null;
      }

      // Ignore browser extension noise (LastPass, Dashlane, etc.)
      // "Object Not Found Matching Id:N, MethodName:X, ParamCount:N"
      if (event.exception?.values?.some(v =>
        v.value?.includes('Object Not Found Matching Id') ||
        v.value?.includes('MethodName:') ||
        v.value?.includes('wrsParams')
      )) {
        return null;
      }

      return event;
    },
  });
}

// Re-export Sentry's ErrorBoundary for use in App.jsx
export const SentryErrorBoundary = Sentry.ErrorBoundary;

// Helper to set user context when they log in
export function setSentryUser(user) {
  if (!SENTRY_DSN) return;
  if (user) {
    Sentry.setUser({ id: user.uid, email: user.email });
  } else {
    Sentry.setUser(null);
  }
}

// Helper to manually capture errors (e.g., in catch blocks)
export function captureError(error, context) {
  if (!SENTRY_DSN) {
    console.error('[Sentry disabled]', error, context);
    return;
  }
  Sentry.captureException(error, { extra: context });
}
